import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AgentRejectionNotice = () => {
  const { user, userProfile, loading: authLoading, sessionChecked } = useAuth();
  const navigate = useNavigate();
  const [agentProfile, setAgentProfile] = useState(null);
  const [rejectedDocuments, setRejectedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Application Status - HoliYay';
  }, []);

  // Check authentication and authorization
  useEffect(() => {
    if (!sessionChecked || authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (userProfile?.role !== 'agent') {
      navigate('/', { replace: true });
      return;
    }

    fetchAgentProfile();
  }, [user, userProfile, authLoading, sessionChecked, navigate]);

  const fetchAgentProfile = async () => {
    try {
      setLoading(true);
      
      // Fetch agent profile
      const { data: profile, error: profileError } = await supabase?.from('agent_profiles')?.select('*')?.eq('user_id', user?.id)?.single();

      if (profileError) throw profileError;

      setAgentProfile(profile);

      // Redirect if not rejected
      if (profile?.status !== 'rejected') {
        if (profile?.status === 'approved') {
          navigate('/agent-dashboard', { replace: true });
        } else {
          navigate('/agent-vetting', { replace: true });
        }
        return;
      }

      // Fetch rejected documents
      const { data: docs, error: docsError } = await supabase?.from('agent_documents')?.select('*')?.eq('agent_id', user?.id)?.eq('status', 'rejected')?.order('created_at', { ascending: false });

      if (docsError) throw docsError;

      setRejectedDocuments(docs || []);
    } catch (error) {
      console.error('[AgentRejectionNotice] Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || authLoading || !sessionChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="bg-card border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="XCircle" size={32} className="text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-foreground mb-2">Application Not Approved</h1>
                <p className="text-muted-foreground mb-4">
                  Thank you for your interest in joining HoliYay as a travel agent. After careful review, we are unable to approve your application at this time.
                </p>
                <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <Icon name="Info" size={16} className="text-red-600" />
                  <p className="text-sm text-red-800">
                    <strong>Current Status:</strong> Application Rejected
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Rejection Reasons Section */}
          {rejectedDocuments?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Reasons for Rejection</h2>
              <div className="space-y-4">
                {rejectedDocuments?.map((doc) => (
                  <div key={doc?.id} className="border border-red-200 bg-red-50 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground mb-1">
                          {doc?.document_type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {doc?.rejection_reason || 'Document did not meet our verification requirements.'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* General Rejection Reasons */}
          {(!rejectedDocuments || rejectedDocuments?.length === 0) && (
            <div className="bg-card border border-border rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Common Reasons for Rejection</h2>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={16} className="text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Incomplete or expired travel agent license documentation
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={16} className="text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Insufficient professional liability insurance coverage
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={16} className="text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Missing or incomplete company registration documents
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={16} className="text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Non-compliance with platform terms and conditions
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps Section */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">What Are Your Options?</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Icon name="RefreshCw" size={16} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">Reapply with Corrections</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    If you can address the issues mentioned above, you're welcome to submit a new application with the correct documentation.
                  </p>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate('/agent-registration')}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Start New Application
                  </Button>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Icon name="MessageCircle" size={16} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">Request Clarification</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    If you have questions about the rejection or need more specific information, our support team is here to help.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    as="a"
                    href="mailto:support@holiyay.com"
                    iconName="Mail"
                    iconPosition="left"
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Icon name="FileText" size={16} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">Review Requirements</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Familiarize yourself with our agent requirements and guidelines before reapplying.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    as="a"
                    href="/agent-requirements"
                    iconName="ExternalLink"
                    iconPosition="right"
                  >
                    View Requirements
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Support Section */}
          <div className="bg-muted/50 rounded-lg p-6">
            <div className="text-center">
              <Icon name="HelpCircle" size={32} className="mx-auto mb-3 text-muted-foreground" />
              <h3 className="font-semibold text-foreground mb-2">Need Help?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our support team is available to answer your questions and provide guidance.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <a
                  href="mailto:support@holiyay.com"
                  className="flex items-center space-x-2 text-orange-600 hover:text-orange-700"
                >
                  <Icon name="Mail" size={16} />
                  <span>support@holiyay.com</span>
                </a>
                <a
                  href="tel:+442012345678"
                  className="flex items-center space-x-2 text-orange-600 hover:text-orange-700"
                >
                  <Icon name="Phone" size={16} />
                  <span>+44 20 1234 5678</span>
                </a>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Business Hours: Monday - Friday, 9:00 AM - 5:00 PM GMT
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentRejectionNotice;